// Attribution tracking utilities for capturing and persisting UTM parameters and other tracking data

interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  sfam_vid?: string;
  ga_client_id?: string;
  session_id?: string;
  landing_page?: string;
  referrer?: string;
  first_touch_source?: string;
  first_touch_date?: string;
  [key: string]: string | undefined;
}

const SFAM_VID_COOKIE = 'sfam_vid';
const SFAM_VID_STORAGE_KEY = 'sfam_vid';
const SFAM_VID_TTL_SECONDS = 13 * 30 * 24 * 60 * 60; // 13 months
const SFAM_VID_PATTERN = /^[0-9a-f]{16}$/i;
const SFAM_SID_COOKIE = 'sfam_sid';
const SFAM_SID_STORAGE_KEY = 'sfam_sid';
const SFAM_SID_TTL_SECONDS = 30 * 60; // 30 minutes
const SFAM_SID_PATTERN = /^[0-9a-f]{32}$/i;
const GA_CLIENT_ID_COOKIE = '_ga';
const LAST_TOUCH_COOKIE_PREFIX = 'last_touch_';
const LAST_TOUCH_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
];

// Cookie utilities
function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

interface CookieOptions {
  maxAgeSeconds?: number;
  domain?: string;
  path?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
}

function setCookieWithOptions(name: string, value: string, options: CookieOptions = {}): void {
  const path = options.path ?? '/';
  let cookie = `${name}=${value};path=${path}`;

  if (options.maxAgeSeconds !== undefined) {
    cookie += `;max-age=${options.maxAgeSeconds}`;
  }
  if (options.domain) {
    cookie += `;domain=${options.domain}`;
  }
  if (options.sameSite) {
    cookie += `;SameSite=${options.sameSite}`;
  }
  if (options.secure) {
    cookie += ';Secure';
  }

  document.cookie = cookie;
}

function getSfamCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  if (
    host === 'wearecnxion.com' ||
    host === 'www.wearecnxion.com' ||
    host.endsWith('.wearecnxion.com')
  ) {
    return '.wearecnxion.com';
  }
  return undefined;
}

function isValidSfamVid(value: string | null): value is string {
  return !!value && SFAM_VID_PATTERN.test(value);
}

function isValidSessionId(value: string | null): value is string {
  return !!value && SFAM_SID_PATTERN.test(value);
}

export function generateSfamVid(): string {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export function getSfamVid(): string | null {
  return getCookie(SFAM_VID_COOKIE);
}

export function getGaClientId(): string | null {
  return getCookie(GA_CLIENT_ID_COOKIE);
}

export function generateSessionId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

function readSessionStorageRecord(): { sid: string; expiresAt: number } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SFAM_SID_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sid?: string; expiresAt?: number } | null;
    if (parsed && typeof parsed.sid === 'string' && typeof parsed.expiresAt === 'number') {
      return { sid: parsed.sid, expiresAt: parsed.expiresAt };
    }
    if (isValidSessionId(raw)) {
      return { sid: raw.toLowerCase(), expiresAt: 0 };
    }
  } catch (e) {
    return null;
  }
  return null;
}

function writeSessionStorageRecord(record: { sid: string; expiresAt: number }): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SFAM_SID_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    // Ignore storage errors (e.g., private browsing)
  }
}

function setSessionCookie(name: string, value: string, maxAgeSeconds: number): void {
  setCookieWithOptions(name, value, {
    maxAgeSeconds,
    domain: getSfamCookieDomain(),
    path: '/',
    sameSite: 'Lax',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
}

function initSessionIdWithState(): { sid: string; isNew: boolean } {
  const now = Date.now();
  let sid = getCookie(SFAM_SID_COOKIE);
  let isNew = false;

  if (!isValidSessionId(sid)) {
    sid = null;
  }

  if (!sid) {
    const stored = readSessionStorageRecord();
    if (stored && stored.expiresAt > now && isValidSessionId(stored.sid)) {
      sid = stored.sid;
    }
  }

  if (!sid) {
    sid = generateSessionId();
    isNew = true;
  }

  sid = sid.toLowerCase();

  const expiresAt = now + SFAM_SID_TTL_SECONDS * 1000;
  writeSessionStorageRecord({ sid, expiresAt });
  setSessionCookie(SFAM_SID_COOKIE, sid, SFAM_SID_TTL_SECONDS);

  return { sid, isNew };
}

export function initSessionId(): string {
  return initSessionIdWithState().sid;
}

export function initSfamVid(): string {
  let vid = getCookie(SFAM_VID_COOKIE);

  if (!isValidSfamVid(vid)) {
    try {
      vid = localStorage.getItem(SFAM_VID_STORAGE_KEY);
    } catch (e) {
      vid = null;
    }
  }

  if (!isValidSfamVid(vid)) {
    vid = generateSfamVid();
  }

  vid = vid.toLowerCase();

  try {
    localStorage.setItem(SFAM_VID_STORAGE_KEY, vid);
  } catch (e) {
    // Ignore storage errors (e.g., private browsing)
  }

  setCookieWithOptions(SFAM_VID_COOKIE, vid, {
    maxAgeSeconds: SFAM_VID_TTL_SECONDS,
    domain: getSfamCookieDomain(),
    path: '/',
    sameSite: 'Lax',
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  });

  return vid;
}

function isSameSiteReferrer(referrer: string): boolean {
  if (!referrer) return true;
  if (typeof window === 'undefined') return true;
  try {
    const refUrl = new URL(referrer);
    const refHost = refUrl.hostname.toLowerCase();
    const host = window.location.hostname.toLowerCase();
    const base = host.replace(/^www\./, '');
    return (
      refHost === host ||
      refHost === base ||
      refHost === `www.${base}` ||
      refHost.endsWith(`.${base}`)
    );
  } catch (e) {
    return true;
  }
}

function getExternalReferrer(): string {
  if (typeof document === 'undefined') return '';
  const referrer = document.referrer || '';
  if (!referrer) return '';
  if (isSameSiteReferrer(referrer)) return '';
  return referrer;
}

function updateLastTouchAttribution(params: URLSearchParams, hasParams: boolean = true): void {
  if (typeof window === 'undefined') return;
  const domain = getSfamCookieDomain();
  const secure = window.location.protocol === 'https:';

  if (hasParams) {
    LAST_TOUCH_PARAMS.forEach(param => {
      const value = params.get(param);
      if (value) {
        setCookieWithOptions(`${LAST_TOUCH_COOKIE_PREFIX}${param}`, value, {
          maxAgeSeconds: SFAM_SID_TTL_SECONDS,
          domain,
          path: '/',
          sameSite: 'Lax',
          secure,
        });
      } else {
        setCookieWithOptions(`${LAST_TOUCH_COOKIE_PREFIX}${param}`, '', {
          maxAgeSeconds: 0,
          domain,
          path: '/',
          sameSite: 'Lax',
          secure,
        });
      }
    });
  }

  setCookieWithOptions(`${LAST_TOUCH_COOKIE_PREFIX}landing_page`, window.location.href, {
    maxAgeSeconds: SFAM_SID_TTL_SECONDS,
    domain,
    path: '/',
    sameSite: 'Lax',
    secure,
  });

  const externalReferrer = getExternalReferrer();
  setCookieWithOptions(`${LAST_TOUCH_COOKIE_PREFIX}referrer`, externalReferrer, {
    maxAgeSeconds: SFAM_SID_TTL_SECONDS,
    domain,
    path: '/',
    sameSite: 'Lax',
    secure,
  });
}

export function getFirstTouchAttribution(): AttributionData {
  const firstTouch: AttributionData = {};
  const firstTouchSource = getCookie('first_touch_source');
  const firstTouchDate = getCookie('first_touch_date');

  if (firstTouchSource) {
    firstTouch.first_touch_source = firstTouchSource;
  }
  if (firstTouchDate) {
    firstTouch.first_touch_date = firstTouchDate;
  }

  return firstTouch;
}

export function getLastTouchAttribution(): AttributionData {
  const lastTouch: AttributionData = {};

  LAST_TOUCH_PARAMS.forEach(param => {
    const value = getCookie(`${LAST_TOUCH_COOKIE_PREFIX}${param}`);
    if (value) {
      lastTouch[param] = value;
    }
  });

  const landingPage = getCookie(`${LAST_TOUCH_COOKIE_PREFIX}landing_page`);
  const referrer = getCookie(`${LAST_TOUCH_COOKIE_PREFIX}referrer`);

  if (landingPage) {
    lastTouch.landing_page = landingPage;
  }
  if (referrer) {
    lastTouch.referrer = referrer;
  }

  return lastTouch;
}

// Main attribution capture function
export function captureAttributionData(): AttributionData {
  const params = new URLSearchParams(window.location.search);
  const attribution: AttributionData = {};

  // List of parameters to capture from URL
  const trackingParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
  ];

  // Capture from URL parameters first, then fall back to cookies
  trackingParams.forEach(param => {
    const urlValue = params.get(param);
    if (urlValue) {
      attribution[param] = urlValue;
      // Persist in cookie for 30 days
      setCookie(param, urlValue, 30);
    } else {
      // Check cookie for previous value
      const cookieValue = getCookie(param);
      if (cookieValue) {
        attribution[param] = cookieValue;
      }
    }
  });

  const currentSfamVid = getSfamVid();
  const sfamVid = isValidSfamVid(currentSfamVid) ? currentSfamVid.toLowerCase() : initSfamVid();
  attribution.sfam_vid = sfamVid;

  const gaClientId = getGaClientId();
  if (gaClientId) {
    attribution.ga_client_id = gaClientId;
  }

  const { sid } = initSessionIdWithState();
  attribution.session_id = sid;
  const hasLastTouchParams = LAST_TOUCH_PARAMS.some(param => params.get(param));
  updateLastTouchAttribution(params, hasLastTouchParams);

  // Set first-touch referrer cookie if not already set
  const externalReferrer = getExternalReferrer();
  if (externalReferrer && !getCookie('ft_ref')) {
    setCookieWithOptions('ft_ref', encodeURIComponent(externalReferrer), {
      maxAgeSeconds: 365 * 24 * 60 * 60, // 1 year
      domain: getSfamCookieDomain(),
      path: '/',
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:',
    });
  }

  // Add context data
  attribution.landing_page = window.location.href;
  attribution.referrer = externalReferrer;

  // First touch attribution (never overwrite)
  const firstTouchSource = getCookie('first_touch_source');
  if (!firstTouchSource && attribution.utm_source) {
    setCookie('first_touch_source', attribution.utm_source, 365); // 1 year
    setCookie('first_touch_date', new Date().toISOString(), 365);
    attribution.first_touch_source = attribution.utm_source;
    attribution.first_touch_date = new Date().toISOString();
  } else if (firstTouchSource) {
    attribution.first_touch_source = firstTouchSource;
    attribution.first_touch_date = getCookie('first_touch_date') || '';
  }

  return attribution;
}

// Populate hidden fields in a form
export function populateHiddenFields(formElement: HTMLFormElement | null): void {
  if (!formElement) return;

  const attribution = captureAttributionData();

  Object.entries(attribution).forEach(([key, value]) => {
    const field = formElement.querySelector<HTMLInputElement>(`input[name="${key}"]`);
    if (field && value) {
      field.value = value;
    }
  });
}

// Get attribution data for form submission
export function getAttributionFormData(): Record<string, string> {
  const attribution = captureAttributionData();
  const lastTouch = getLastTouchAttribution();
  const mergedAttribution = { ...attribution, ...lastTouch };
  const formData: Record<string, string> = {};

  // Only include non-empty values
  Object.entries(mergedAttribution).forEach(([key, value]) => {
    if (value) {
      formData[key] = value;
    }
  });

  // Add timestamp
  formData.form_submitted_at = new Date().toISOString();

  return formData;
}

// Identity-only payload (sfam_vid, ga_client_id, session_id)
export function getIdentityFormData(): Record<string, string> {
  const attribution = captureAttributionData();
  const lastTouch = getLastTouchAttribution();
  const identity: Record<string, string> = {};

  if (attribution.sfam_vid) {
    identity.sfam_vid = attribution.sfam_vid;
  }
  if (attribution.ga_client_id) {
    identity.ga_client_id = attribution.ga_client_id;
  }
  if (attribution.session_id) {
    identity.session_id = attribution.session_id;
  }

  if (lastTouch.utm_source) {
    identity.last_touch_source = lastTouch.utm_source;
  }
  if (lastTouch.utm_medium) {
    identity.last_touch_medium = lastTouch.utm_medium;
  }
  if (lastTouch.utm_campaign) {
    identity.last_touch_campaign = lastTouch.utm_campaign;
  }
  if (lastTouch.utm_term) {
    identity.last_touch_term = lastTouch.utm_term;
  }
  if (lastTouch.utm_content) {
    identity.last_touch_content = lastTouch.utm_content;
  }
  if (lastTouch.gclid) {
    identity.last_touch_gclid = lastTouch.gclid;
  }
  if (lastTouch.fbclid) {
    identity.last_touch_fbclid = lastTouch.fbclid;
  }
  if (lastTouch.msclkid) {
    identity.last_touch_msclkid = lastTouch.msclkid;
  }
  if (lastTouch.landing_page) {
    identity.last_touch_landing_page = lastTouch.landing_page;
  }
  if (lastTouch.referrer) {
    identity.last_touch_referrer = lastTouch.referrer;
  }

  return identity;
}

/**
 * Push attribution data to dataLayer for GTM
 * @deprecated Use useAnalytics().trackFormEvent() instead - attribution data is now
 * automatically included in the consolidated dataLayer event
 */
export function pushAttributionToDataLayer(eventName: string, formType: string): void {
  const attribution = captureAttributionData();

  if (typeof window !== 'undefined' && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: eventName,
      form_type: formType,
      sfam_vid: attribution.sfam_vid,
      attribution: {
        source: attribution.utm_source,
        medium: attribution.utm_medium,
        campaign: attribution.utm_campaign,
        keyword: attribution.utm_term,
        ad_content: attribution.utm_content,
        gclid: attribution.gclid,
        landing_page: attribution.landing_page,
        referrer: attribution.referrer,
        first_touch_source: attribution.first_touch_source,
      },
    });
  }
}

// Default Google Ads Conversion ID
const GOOGLE_ADS_CONVERSION_ID = 'AW-764093659';

// Fire Google Ads conversion if GCLID present
export function fireGoogleAdsConversion(
  conversionId: string = GOOGLE_ADS_CONVERSION_ID,
  value: number = 1.0
): void {
  const attribution = captureAttributionData();

  if (attribution.gclid && typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      send_to: conversionId,
      value: value,
      currency: 'USD',
    });
  }
}
